import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  STAGE1_COLUMNS,
  STAGE1_MAX_TASKS_PER_COLUMN,
  STAGE1_TASKS,
  canPlaceTaskInColumn,
  validateTaskTriage,
} from "./stage1TaskData";
import {
  clearEmptyDragImage,
  mountDragVisual,
  moveDragVisual,
  setEmptyDragImage,
  unmountDragVisual,
} from "./stage1DragVisual";

function findDropzoneAt(clientX, clientY) {
  const el = document.elementFromPoint(clientX, clientY);
  const zone = el?.closest("[data-dropzone]");
  return zone?.dataset.dropzone || null;
}

function TaskCard({
  task,
  variant = "default",
  isDragging,
  usePointerDrag,
  onDragStart,
  onDrag,
  onDragEnd,
  onPointerDragStart,
}) {
  const compact = variant === "compact";
  const square = variant === "square";

  const handleDragStart = (e) => {
    if (usePointerDrag) return;
    const sourceEl = e.currentTarget;
    setEmptyDragImage(e);
    mountDragVisual(sourceEl, e.clientX, e.clientY, {
      floatClass: "stage1-task__drag-float",
      draggingClass: "stage1-task--is-dragging",
    });

    e.dataTransfer.setData("text/task-id", task.id);
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "move";
    onDragStart?.(task.id);
  };

  const handleDrag = (e) => {
    if (usePointerDrag) return;
    moveDragVisual(e.clientX, e.clientY);
    onDrag?.(e);
  };

  const handleDragEnd = (e) => {
    if (usePointerDrag) return;
    unmountDragVisual();
    onDragEnd?.(e);
  };

  const handlePointerDown = (e) => {
    if (!usePointerDrag || !e.isPrimary) return;
    e.preventDefault();
    e.stopPropagation();
    onPointerDragStart?.(task.id, e.clientX, e.clientY, e.currentTarget);
  };

  return (
    <div
      className={`stage1-task${compact ? " stage1-task--compact" : ""}${
        square ? " stage1-task--square" : ""
      }${isDragging ? " stage1-task--is-dragging" : ""}`}
      style={task.accent ? { "--task-accent": task.accent } : undefined}
      draggable={!usePointerDrag}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onPointerDown={handlePointerDown}
    >
      <span className="stage1-task__icon" aria-hidden>
        {task.icon}
      </span>
      <div className="stage1-task__body">
        <div className="stage1-task__title">{task.title}</div>
      </div>
      {!compact ? (
        <span className="stage1-task__grip" aria-hidden>⋮</span>
      ) : null}
    </div>
  );
}

export default function Stage1TaskTriage({
  onSubmit,
  submitting = false,
  isMobilePortrait = false,
}) {
  const [assignments, setAssignments] = useState({});
  const [draggingId, setDraggingId] = useState(null);
  const [activeDropzone, setActiveDropzone] = useState(null);
  const advancedRef = useRef(false);
  const pointerDragCleanupRef = useRef(null);
  const assignmentsRef = useRef(assignments);

  assignmentsRef.current = assignments;

  const usePointerDrag = useMemo(() => {
    if (isMobilePortrait) return true;
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window && window.innerWidth <= 900;
  }, [isMobilePortrait]);

  const poolTasks = useMemo(
    () => STAGE1_TASKS.filter((t) => !assignments[t.id]),
    [assignments]
  );

  const tasksByColumn = useMemo(() => {
    const map = Object.fromEntries(STAGE1_COLUMNS.map((c) => [c.id, []]));
    STAGE1_TASKS.forEach((task) => {
      const col = assignments[task.id];
      if (col && map[col]) map[col].push(task);
    });
    return map;
  }, [assignments]);

  const finishDrag = useCallback(() => {
    pointerDragCleanupRef.current?.();
    pointerDragCleanupRef.current = null;
    clearEmptyDragImage();
    unmountDragVisual();
    setDraggingId(null);
    setActiveDropzone(null);
  }, []);

  const placeTask = useCallback((taskId, dropzoneId) => {
    if (!taskId || !dropzoneId) return;

    if (dropzoneId === "pool") {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
      return;
    }

    setAssignments((prev) => {
      if (!canPlaceTaskInColumn(prev, taskId, dropzoneId)) return prev;
      return { ...prev, [taskId]: dropzoneId };
    });
  }, []);

  const handleDragStart = useCallback((taskId) => {
    setActiveDropzone(null);
    window.requestAnimationFrame(() => setDraggingId(taskId));
  }, []);

  const handleDragEnd = useCallback(() => {
    finishDrag();
  }, [finishDrag]);

  const handleDropOnColumn = useCallback(
    (columnId, e) => {
      e.preventDefault();
      const taskId =
        e.dataTransfer.getData("text/task-id") ||
        e.dataTransfer.getData("text/plain");
      if (!taskId) return;
      placeTask(taskId, columnId);
      finishDrag();
    },
    [finishDrag, placeTask]
  );

  const handleDropOnPool = useCallback(
    (e) => {
      e.preventDefault();
      const taskId =
        e.dataTransfer.getData("text/task-id") ||
        e.dataTransfer.getData("text/plain");
      if (!taskId) return;
      placeTask(taskId, "pool");
      finishDrag();
    },
    [finishDrag, placeTask]
  );

  const handleDragOverColumn = useCallback(
    (columnId, e) => {
      e.preventDefault();
      const taskId =
        draggingId ||
        e.dataTransfer.getData("text/task-id") ||
        e.dataTransfer.getData("text/plain");
      if (
        taskId &&
        !canPlaceTaskInColumn(assignmentsRef.current, taskId, columnId)
      ) {
        e.dataTransfer.dropEffect = "none";
        setActiveDropzone(null);
        moveDragVisual(e.clientX, e.clientY);
        return;
      }
      e.dataTransfer.dropEffect = "move";
      setActiveDropzone(columnId);
      moveDragVisual(e.clientX, e.clientY);
    },
    [draggingId]
  );

  const handleDragOverPool = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setActiveDropzone("pool");
    moveDragVisual(e.clientX, e.clientY);
  }, []);

  const handlePointerDragStart = useCallback(
    (taskId, clientX, clientY, sourceEl) => {
      if (draggingId) return;

      mountDragVisual(sourceEl, clientX, clientY, {
        floatClass: "stage1-task__drag-float",
        draggingClass: "stage1-task--is-dragging",
      });
      setActiveDropzone(null);
      setDraggingId(taskId);

      const onMove = (e) => {
        if (!e.isPrimary) return;
        e.preventDefault();
        moveDragVisual(e.clientX, e.clientY);
        const dropzoneId = findDropzoneAt(e.clientX, e.clientY);
        if (
          dropzoneId &&
          dropzoneId !== "pool" &&
          !canPlaceTaskInColumn(assignmentsRef.current, taskId, dropzoneId)
        ) {
          setActiveDropzone(null);
          return;
        }
        setActiveDropzone(dropzoneId);
      };

      const onEnd = (e) => {
        if (!e.isPrimary) return;
        const dropzoneId = findDropzoneAt(e.clientX, e.clientY);
        if (dropzoneId) {
          placeTask(taskId, dropzoneId);
        }
        finishDrag();
      };

      document.addEventListener("pointermove", onMove, { passive: false });
      document.addEventListener("pointerup", onEnd);
      document.addEventListener("pointercancel", onEnd);

      pointerDragCleanupRef.current = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onEnd);
        document.removeEventListener("pointercancel", onEnd);
      };
    },
    [draggingId, finishDrag, placeTask]
  );

  useEffect(() => {
    if (!draggingId || usePointerDrag) return undefined;

    const onDragOver = (e) => {
      e.preventDefault();
      moveDragVisual(e.clientX, e.clientY);
    };

    document.addEventListener("dragover", onDragOver);
    return () => document.removeEventListener("dragover", onDragOver);
  }, [draggingId, usePointerDrag]);

  useEffect(() => {
    return () => {
      pointerDragCleanupRef.current?.();
      unmountDragVisual();
    };
  }, []);

  useEffect(() => {
    if (submitting || advancedRef.current) return;
    if (!validateTaskTriage(assignments).allPlaced) return;

    advancedRef.current = true;
    onSubmit?.(assignments);
  }, [assignments, onSubmit, submitting]);

  return (
    <div
      className={`stage1-triage${draggingId ? " stage1-triage--dragging" : ""}${
        isMobilePortrait ? " stage1-triage--mobile-portrait" : ""
      }`}
    >
      <header className="stage1-triage__header">
        <h2 className="stage1-triage__title">PRIORITY ORDER</h2>
        <p className="stage1-triage__subtitle">
          {isMobilePortrait
            ? "Drag tasks to arrange from top to bottom"
            : "Drag tasks into the order that works for you"}
        </p>
      </header>

      <div className="stage1-triage__body">
        <div className="stage1-triage__priorities">
          {STAGE1_COLUMNS.map((col, index) => {
            const columnTasks = tasksByColumn[col.id];
            const columnFull =
              columnTasks.length >= STAGE1_MAX_TASKS_PER_COLUMN;
            const canAcceptDrop =
              !draggingId ||
              canPlaceTaskInColumn(assignments, draggingId, col.id);
            return (
              <div key={col.id} className="stage1-triage__slot">
                <div
                  className="stage1-triage__slot-label"
                  style={{ backgroundColor: col.color }}
                >
                  <span className="stage1-triage__slot-num">{index + 1}</span>
                  <div className="stage1-triage__slot-text">
                    <span className="stage1-triage__slot-title">{col.subtitle}</span>
                    <span className="stage1-triage__slot-hint">{col.hint}</span>
                  </div>
                </div>
                <div
                  className={`stage1-triage__dropzone${
                    draggingId ? " stage1-triage__dropzone--active" : ""
                  }${
                    activeDropzone === col.id ? " stage1-triage__dropzone--target" : ""
                  }${
                    draggingId && columnFull && !canAcceptDrop
                      ? " stage1-triage__dropzone--full"
                      : ""
                  }${columnTasks.length === 0 ? " stage1-triage__dropzone--empty" : ""}`}
                  style={{
                    borderColor: col.color,
                    "--dropzone-color": col.color,
                  }}
                  data-dropzone={col.id}
                  onDragOver={(e) => handleDragOverColumn(col.id, e)}
                  onDrop={(e) => handleDropOnColumn(col.id, e)}
                >
                  {columnTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      variant="compact"
                      isDragging={draggingId === task.id}
                      usePointerDrag={usePointerDrag}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onPointerDragStart={handlePointerDragStart}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="stage1-triage__center">
          <div className="stage1-triage__center-top">
            <div className="stage1-triage__frog" aria-hidden>
              🐸
            </div>
            <p className="stage1-triage__quote">
              The key is not to prioritize what&apos;s on your schedule, but to
              schedule your priorities.
            </p>
          </div>
          <div className="stage1-triage__tip-box stage1-triage__tip-box--center">
            <span className="stage1-triage__tip-icon" aria-hidden>
              💡
            </span>
            <span>
              TIP: Eat the frog = Do your hardest or most important task first each
              day!
            </span>
          </div>
        </div>

        <div className="stage1-triage__pool">
          {/* <div className="stage1-triage__pool-header">
            <h3 className="stage1-triage__pool-title">Available Tasks</h3>
            <p className="stage1-triage__pool-hint">Drag to priority slots</p>
          </div> */}
          <div
            className={`stage1-triage__pool-grid${
              activeDropzone === "pool" ? " stage1-triage__pool-grid--target" : ""
            }`}
            data-dropzone="pool"
            onDragOver={handleDragOverPool}
            onDrop={handleDropOnPool}
          >
            {poolTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                variant="square"
                isDragging={draggingId === task.id}
                usePointerDrag={usePointerDrag}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onPointerDragStart={handlePointerDragStart}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
